// ─────────────────────────────────────────────────────────────────────────────
//  PLACEHOLDER COPY — pending Ted Carstensen
// ─────────────────────────────────────────────────────────────────────────────
//
// Every user-facing string on the Heavybit Identity front door that was
// written by an engineer rather than by the person who owns Heavybit's voice
// lives in THIS FILE and nowhere else. Swapping it is editing one object; no
// component, test or copy decision is spread across the codebase.
//
// Rules the replacement must keep, because they are product decisions and not
// wording:
//   - The waitlist state is NOT an error. The person signed in successfully.
//     No red, no "denied", no "failed", no "unauthorized".
//   - "identityNotLinked" must send the person to the account they already
//     have, must NOT name a provider, and must not imply wrongdoing. Staff
//     arrive through Google, but a Crucible person may have linked through
//     GitHub, so "your Heavybit Google account" was wrong for a whole class
//     of the people who read it — the same mistake as the work-account lede
//     removed from the sign-in page in 5e8cea11b.
//   - Nothing here may promise a date, a decision, or a service level.
//   - The consent screen names the account and offers a way out, but does NOT
//     force a re-choice the way the sign-in page does. A correct session is the
//     common case; re-authenticating every agent connection is a real cost.
//
// Tests assert the SHAPE (which key is rendered where), never the words, so
// rewording cannot break the build.

export const IDENTITY_COPY = {
  /** The brand-neutral shell every /identity/* page renders inside
   *  (components/identity/IdentityFrame.vue). `logoLabel` is the accessible
   *  name on the Heavybit lockup link at the top, which is a link to
   *  heavybit.com, not to anything in this app. The lockup is what says
   *  whose account system this is — Heavybit, not Auger — for
   *  every page this shell renders; a small-caps eyebrow line saying the
   *  same thing in words used to sit under it (`brand`, here) but was
   *  removed once the logo made it redundant: logo -> "Heavybit Identity" ->
   *  (the eyebrow said "Heavybit Identity"; the product people see is just
   *  "Heavybit")
   *  a page title like "Sign in" said the same thing twice. */
  frame: {
    logoLabel: "Heavybit home",
  },

  /** The sign-in page's LEDE, directly under the heading and above the
   *  buttons.
   *
   *  It used to carry a second sentence — "Only your name and email address
   *  are stored" — which was not true, and was hardcoded in
   *  IdentityLogin.vue's template rather than living here. Both of those are
   *  why it survived: a factual claim about data handling, written by an
   *  engineer, sitting outside the one file that is supposed to hold every
   *  such string and is queued for a copy review.
   *
   *  First sign-in writes more than a name and an email. The `users` row also
   *  gets a slug, `class` (staff or network — whether the person is Heavybit
   *  or outside it), `signup_source` (which surface they arrived from),
   *  `active`, timestamps, and `stytch_member_id` moments later. Stytch holds
   *  the member record, which provider was used, and session data including
   *  the IP and user agent this project deliberately does NOT store itself.
   *  PostHog holds the /identity/* pageview, and for a Crucible signup a
   *  person profile carrying the email.
   *
   *  No replacement claim is made here. What is stored is the privacy
   *  policy's job, it is linked two lines above this, and a short version
   *  that is both accurate and worth reading does not exist — every attempt
   *  is either a list nobody finishes or a vaguer version of the same
   *  promise. Do not reintroduce one without legal review. */
  login: {
    /** Deliberately NOT in the legal notice and NOT styled as small print.
     *  Creating an account on first sign-in is a thing that happens TO the
     *  person; it is not a term they are agreeing to, and burying it beside
     *  the privacy and terms links said that it was. It sits above the
     *  buttons, at body size and full contrast, for that reason. */
    lede: "New here? We'll create your account when you sign in.",
  },

  /** The clickwrap NOTICE on the sign-in page (IdentityLogin.vue only). Not
   *  recorded consent — the app doesn't know who this is yet at the moment
   *  this renders, identity only arrives from Stytch after they sign in, so
   *  nothing here is or can be logged against anybody. See
   *  IdentityLogin.vue's own comment for why a consent gate does not belong
   *  on this page; that's a separate, unbuilt mechanism.
   *
   *  Split into parts rather than one string with embedded markup, so the
   *  component renders two real `<a>` elements instead of parsing HTML out
   *  of copy. `before`/`between`/`after` each carry their own surrounding
   *  punctuation and spacing (no space is added in the template) so the
   *  sentence reads correctly when concatenated around the two links —
   *  changing the wording only means editing these strings, never the
   *  component.
   *
   *  The terms are SCOPED ("where they apply"), the privacy policy is NOT —
   *  this is a product decision, not wording, and any rewrite must keep it.
   *  The Crucible Terms of Service define "the Service" as
   *  crucible.heavybit.com/mcp plus the Heavybit Library and never mention
   *  Auger, but this same sign-in page also serves Auger staff — an
   *  unscoped "our terms" would tell a staff member they'd just agreed to a
   *  document that does not govern them. The privacy policy is
   *  company-wide, so it is the one unconditional half of the sentence.
   *  The link said "Crucible Terms of Service" until 2026-09-22, so that the
   *  scope was legible from the link alone and not only from surrounding
   *  prose a screen reader might not read as one unit. It is now a bare
   *  "Terms of Service": the people signing in here are largely external and
   *  do not know what Crucible is, so the product name asked them to carry a
   *  distinction that means nothing to them. The scoping itself is unchanged
   *  and still load-bearing — it lives in "where they apply", which is why
   *  that phrase must survive any rewrite.
   *
   *  What this costs, recorded rather than waved away: somebody reading a
   *  screen reader's link list out of context now sees "Terms of Service"
   *  with no scope attached. That is the reason the old label existed. If a
   *  second product ever publishes its own terms, this decision needs
   *  revisiting rather than extending.
   *
   *  BOTH URLs ARE ON crucible.heavybit.com, decided 2026-09-22. The rule
   *  behind it is that terms and a privacy policy belong on the domain they
   *  pertain to. Applied strictly that rule splits these two — the terms are
   *  Crucible's, but the privacy policy's own text covers "Heavybit, Inc.
   *  and its subsidiaries and affiliates" across the website, the Library,
   *  live events and marketing, which is the company and not Crucible. It is
   *  on the Crucible domain anyway, as a deliberate call: heavybit.com has no
   *  legal pages at all and no route to serve one, and a policy nobody can
   *  read is worse than a policy on a narrower domain than its contents.
   *  Revisit when heavybit.com can host it.
   *
   *  These are NOT Sanity pages. Both documents are markdown committed to the
   *  crucible repo (src/legal/) and rendered by its Hono app, so the text has
   *  a reviewable history and `content_sha256` can be reproduced from any
   *  commit with `shasum -a 256`. The Sanity drafts that used to hold this
   *  text are abandoned; do not point these URLs back at them.
   *
   *  The consent catalog keys on that hash, so changing either URL here
   *  WITHOUT a corresponding consent_documents row is how somebody ends up
   *  agreeing to a page nobody recorded. */
  loginLegal: {
    before: "By continuing, I agree to Heavybit's ",
    privacyPolicyLabel: "privacy policy",
    privacyPolicyUrl: "https://crucible.heavybit.com/privacy",
    between: " and ",
    termsOfServiceLabel: "terms of service",
    termsOfServiceUrl: "https://crucible.heavybit.com/terms",
    after: ".",
  },

  /** Refused at the exchange: this provider identity has no Heavybit account
   *  and we will not create a second one. Reason `identity_not_linked`.
   *
   *  Names no provider on purpose (see the rules in the header): the person
   *  reading this has just arrived through one provider and needs to be sent
   *  to whichever other one their account is on, which this side cannot
   *  know. "The account you already use" covers Google and GitHub alike. */
  identityNotLinked:
    "Sign in with the account you already use for Heavybit. If you think this is wrong, contact Heavybit.",

  /** The landing page for a successful sign-in with no access yet. */
  waitlist: {
    title: "You're on the list",
    lede: "Thanks for signing in. Your account is set up, and we'll be in touch when there's something here for you.",
    detail: "Nothing went wrong — there's just nothing to show you yet.",
    /** Naming the account is what makes a wrong-account sign-in diagnose
     *  itself. Somebody who registered with a personal address, and whose
     *  browser had a live work Google session, otherwise sees this page with
     *  nothing on it that says which identity it is about. Still not an
     *  error: a label, not a warning. */
    signedInAs: "Signed in as",
    /** The way back, in one line. Signing out lands on the sign-in page, which
     *  always asks which account to use, so there is nothing else to do and no
     *  second button that would mean the same thing. */
    wrongAccount: "Not the account you meant? Sign out — you'll be asked which account to use.",
    signOut: "Sign out",
  },

  /** A successful sign-in by somebody whose Heavybit account works — it just
   *  is not an Auger account. Distinct from `waitlist` because they are not
   *  waiting for anything and there is nothing to be added to: on 2026-09-21
   *  ten production accounts held `library`, a Crucible entitlement, and every
   *  one of them was told they were on a list.
   *
   *  Names no product and links nowhere, deliberately. The token carries grant
   *  STRINGS, not products, so naming Crucible would mean a label map in the
   *  browser — a second copy of `grant_types` that drifts — or grant products
   *  added to the Stytch template. Neither earns its keep for one sentence.
   *
   *  Same rules as `waitlist` above: not an error, nothing promised. */
  noAugerAccess: {
    title: "This account doesn't include Auger",
    lede: "Your Heavybit account is active. It just doesn't have access to this application.",
    signedInAs: "Signed in as",
    wrongAccount: "Not the account you meant? Sign out — you'll be asked which account to use.",
    signOut: "Sign out",
  },

  /** The Connected Apps consent screen. This is the only page in the flow that
   *  mints a durable agent token today, so it is the one place the account MUST
   *  be named: "X is asking to act on your behalf" is a question about identity
   *  that the page was answering silently.
   *
   *  The not-forcing-a-re-choice decision is in the header rules above; it is a
   *  product decision, not wording. */
  authorize: {
    signedInAs: "Signed in as",
    /** Stands in for the scope list that used to be here. The raw scope
     *  strings (`auger:read`, `crucible:read`) read like a permissions model
     *  that does not exist — there is no matching `:write`, and the real
     *  per-person permissions ride on the person's grants, not on these — so
     *  they were removed rather than explained.
     *
     *  This one line survives them because it carried something no other part
     *  of the page says. Scopes are RBAC scopes: a person can only grant a
     *  scope their roles hold, so `is_grantable: false` means THIS account
     *  cannot complete THIS request. That is most often the wrong account,
     *  which is why the line points at the switch below it instead of naming
     *  a scope. Without it, approving is a silent dead end. */
    cannotGrantAll:
      "This account can't grant everything this application is asking for. If that's a surprise, you may be signed in as the wrong account.",
    /** Heads the scope checkboxes, shown when the client requests resource
     *  scopes (the permission catalogue). The client is named in the line
     *  above, so this says only what the ticks decide. */
    chooseScopes: "Choose what it may do. You can approve some and leave the rest.",
    /** Below the choices when the client asked for scopes this account's
     *  roles do not cover. Those are not listed (a row nobody can tick is
     *  noise); this line says that something was left out. */
    someNotGrantable: "Some permissions are not available to your account.",
    /** Approve with nothing ticked would give the client a token that can
     *  do nothing, so the button waits for one tick. */
    noneChosen: "Choose at least one, or deny.",
    /** When the SDK cannot answer. Saying nothing would answer the identity
     *  question silently all over again, which is the bug this line fixes. */
    accountUnknown: "We could not confirm which account you're signed in as.",
    /** Deliberately a question, not a warning. Most people reading it are
     *  signed in as the right account and should feel free to ignore it.
     *  Terser than the waitlist's `wrongAccount` on purpose: this one sits
     *  inline next to the address, with the explanation beside it. */
    notYou: "Not you?",
    /** Rendered as VISIBLE text, not a tooltip — it is what lets `notYou` be a
     *  bare question rather than a warning, in front of a control that ends a
     *  live session. Deliberately not "this request will still be here": the
     *  return path is held in sessionStorage, and when that is unavailable the
     *  callback falls back to the done page, so a guarantee is one the system
     *  cannot always keep. */
    switchAccount: "Signing out takes you back here to finish.",
    /** The sign-out worked but the sign-in page did not open. Says what is true
     *  and gives the one action that recovers it. */
    switchFailed: "You're signed out, but the sign-in page didn't open. Reload to continue.",
    /** `oauthAuthorizeStart` refused or failed. This is NOT a sign-in failure,
     *  and it used to say it was: the shared exchange describer's fallback is
     *  "Sign-in failed. Please try again.", which is wrong twice over on this
     *  page. Sign-in SUCCEEDED — the person is holding a live session, which
     *  is why the account line and the switch link render right beneath this
     *  message. And "please try again" is advice that cannot work for the
     *  failure we have actually seen.
     *
     *  Observed 2026-09-17: a Network-org member authorizing the first-party
     *  Auger CLI, refused because the organization's
     *  `first_party_connected_apps_allowed_type` is NOT_ALLOWED. That is
     *  permanent for that account and that application. Retrying is an
     *  infinite loop with no exit.
     *
     *  Both halves are here because the specific Stytch `error_type` for the
     *  policy refusal is not documented and has not been captured, so this
     *  copy has to be true whether the cause is permanent or transient.
     *  Retrying is still offered, but second, and hedged. When the error type
     *  is known, give it its own case in describeConsentError and make this
     *  the fallback it already is. */
    startFailed:
      "This application couldn't be authorized for this account. If you have another Heavybit account, sign out below and use that one. Otherwise contact Heavybit — retrying may not help.",
  },
} as const
